<template>
    <div class="relative bg-white">
        <Hero
            :title="$t('our_services.flagship_service')"
            :lead="$t('indian_dentists.lead')"
            :description="$t('indian_dentists.description')"
            :illustration="require('~/assets/images/illustrations/indian-dentists/dentist-man.png')"
            :decoration="require('~/assets/images/illustrations/decorator-dots-yellow.svg')"
            secondary-type
            :primary-button="{
                label: $t('generic.contact_cta_variants.variant_1'),
            }"
            @primary-button-click="openContactModal"
        />

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Intro */}}
        {{/* * */}}
        <div class="container text-black pb-16 md:pt-8">
            <div class="grid grid-cols-12 gap-5">
                <div class="col-span-12 lg:col-span-10 lg:col-start-2 xl:col-span-8 xl:col-start-3">
                    <h2 class="title title-md text-center mb-10 max-w-xl mx-auto">
                        {{ $t('indian_dentists.intro.title') }}
                    </h2>
                    <p class="sm:text-center">{{ $t('indian_dentists.intro.paragraph_1') }}</p>
                </div>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Why indian dentists */}}
        {{/* * */}}
        <div class="bg-gray-100 text-black py-16 md:py-20">
            <SectionWrapper :title="$t('indian_dentists.pros.title')" centered-title class="container">
                <div class="grid grid-cols-12">
                    <div class="col-span-12 lg:col-span-8 lg:col-start-3">
                        <p class="sm:text-center mb-6">
                            {{ $t('indian_dentists.pros.description_1') }}
                        </p>
                        <p class="sm:text-center mb-14">
                            {{ $t('indian_dentists.pros.description_2') }}
                        </p>
                    </div>
                    <div class="col-span-12">
                        <div class="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10 lg:max-w-[90%] mx-auto mb-4">
                            <div class="col-span-2 lg:col-span-1">
                                <h3 class="sm:text-center title title-base text-purple-400 mb-5">
                                    {{ $t('indian_dentists.pros.pro_1.title') }}
                                </h3>
                                <p class="sm:text-center">
                                    {{ $t('indian_dentists.pros.pro_1.description') }}
                                </p>
                            </div>
                            <div class="col-span-2 lg:col-span-1">
                                <h3 class="sm:text-center title title-base text-purple-400 mb-5">
                                    {{ $t('indian_dentists.pros.pro_2.title') }}
                                </h3>
                                <p class="sm:text-center">
                                    {{ $t('indian_dentists.pros.pro_2.description') }}
                                </p>
                            </div>
                            <div class="col-span-2 lg:col-span-1">
                                <h3 class="sm:text-center title title-base text-purple-400 mb-5">
                                    {{ $t('indian_dentists.pros.pro_3.title') }}
                                </h3>
                                <p class="sm:text-center">
                                    {{ $t('indian_dentists.pros.pro_3.description') }}
                                </p>
                            </div>
                            <div class="col-span-2 lg:col-span-1">
                                <h3 class="sm:text-center title title-base text-purple-400 mb-5">
                                    {{ $t('indian_dentists.pros.pro_4.title') }}
                                </h3>
                                <p class="sm:text-center">
                                    {{ $t('indian_dentists.pros.pro_4.description') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="text-center mt-8 lg:mt-12">
                    <button type="button" class="p-component p-button p-button-primary" @click="openContactModal">
                        <span class="p-button-label"> {{ $t('generic.contact_us') }} </span>
                    </button>
                </div>
            </SectionWrapper>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Case study highlight */}}
        {{/* * */}}
        <div class="purple-gradient-bg text-white pt-16 md:pt-20 pb-10 md:pb-14">
            <div class="container">
                <FeatureCard
                    :title="$t('indian_dentists.case_study.cta_title')"
                    :illustration="require('~/assets/images/illustrations/indian-dentists/dentistry.png')"
                    :decorator="require('~/assets/images/illustrations/decorator-dots-magenta.svg')"
                    rounded-t-l
                    with-gap-between-text-and-image
                >
                    <p class="mb-10">{{ $t('indian_dentists.case_study.highlight') }}</p>

                    <div class="flex flex-col sm:flex-row gap-6 dark">
                        <ContactDetail
                            :icon="require('~/assets/images/icons/mail-blue.svg')"
                            :label="$t('contact.email') + ':'"
                            white
                        >
                            <a href="mailto:info@banyaiconsulting.hu">info@banyaiconsulting.hu</a>
                        </ContactDetail>
                        <ContactDetail
                            :icon="require('~/assets/images/icons/phone-blue.svg')"
                            :label="$t('contact.phone') + ':'"
                            white
                        >
                            <a href="tel:+3619506081">+36 1 950 60 81</a>
                        </ContactDetail>
                    </div>
                </FeatureCard>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Two countries, one coordinated service */}}
        {{/* * */}}
        <div class="relative py-16 lg:py-20 bg-gray-800 text-white">
            <div class="container">
                <div class="aux-text text-center mb-4">{{ $t('indian_dentists.background.eyebrow') }}</div>
                <SectionWrapper :title="$t('indian_dentists.background.title')" centered-title>
                    <div class="grid grid-cols-1 gap-y-5 md:gap-y-16 lg:gap-y-20 pt-5 sm:pt-12">
                        <FeatureCard
                            :index="1"
                            :title="$t('indian_dentists.background.item_1.title')"
                            :content="$t('indian_dentists.background.item_1.description')"
                            :illustration="require('~/assets/images/illustrations/indian-dentists/airplane.png')"
                            :decorator="require('~/assets/images/illustrations/decorator-dots-magenta.svg')"
                            rounded-b-r
                            with-contact-button
                            :contact-btn-label="$t('generic.contact_cta_variants.variant_1')"
                            contact-opens-modal
                            with-gap-between-text-and-image
                            @contact-click="openContactModal"
                        />

                        <FeatureCard
                            :index="2"
                            :title="$t('indian_dentists.background.item_2.title')"
                            :description="$t('indian_dentists.background.item_2.description')"
                            :illustration="require('~/assets/images/illustrations/indian-dentists/business-woman.png')"
                            :decorator="require('~/assets/images/illustrations/decorator-dots-yellow.svg')"
                            reversed
                            rounded-t-l
                            with-contact-button
                            :contact-btn-label="$t('generic.contact_cta_variants.variant_2')"
                            contact-opens-modal
                            with-gap-between-text-and-image
                            @contact-click="openContactModal"
                        />

                        <FeatureCard
                            :index="3"
                            :title="$t('indian_dentists.background.item_3.title')"
                            :content="$t('indian_dentists.background.item_3.description')"
                            :illustration="require('~/assets/images/illustrations/indian-dentists/business-man.png')"
                            :decorator="require('~/assets/images/illustrations/decorator-dots-magenta.svg')"
                            rounded-t-r
                            with-contact-button
                            :contact-btn-label="$t('generic.contact_cta_variants.variant_3')"
                            contact-opens-modal
                            with-gap-between-text-and-image
                            @contact-click="openContactModal"
                        />

                        <FeatureCard
                            :index="4"
                            :title="$t('indian_dentists.background.item_4.title')"
                            :content="$t('indian_dentists.background.item_4.description')"
                            :illustration="require('~/assets/images/illustrations/indian-dentists/eu-pass.jpg')"
                            :decorator="require('~/assets/images/illustrations/decorator-dots-yellow.svg')"
                            reversed
                            rounded-t-l
                            with-contact-button
                            :contact-btn-label="$t('generic.contact_cta_variants.variant_4')"
                            contact-opens-modal
                            with-gap-between-text-and-image
                            @contact-click="openContactModal"
                        />
                    </div>
                </SectionWrapper>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Next step */}}
        {{/* * */}}
        <div class="purple-gradient-bg text-white pt-16 md:pt-20 pb-20 md:pb-28">
            <div class="container">
                <SectionWrapper :title="$t('indian_dentists.next_step.title')" centered-title class="mb-16 md:mb-20">
                    <p class="sm:text-center max-w-2xl mx-auto">{{ $t('indian_dentists.next_step.description') }}</p>
                </SectionWrapper>

                <div
                    class="
                        bg-white
                        rounded-tr-100 rounded-bl-100
                        lg:rounded-tr-150 lg:rounded-bl-150
                        py-12
                        px-5
                        md:py-16 md:px-10
                    "
                >
                    <SectionWrapper :title="$t('indian_dentists.next_step.contact_title')" theme-light centered-title>
                        <div class="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-5xl mx-auto">
                            <ContactDetail
                                :icon="require('~/assets/images/icons/location-blue.svg')"
                                :label="$t('generic.company_name')"
                                text-black
                            >
                                1012 Budapest,<br />Logodi utca 44/b. 1. em. 4.
                            </ContactDetail>
                            <ContactDetail
                                :icon="require('~/assets/images/icons/mail-blue.svg')"
                                :label="$t('contact.email') + ':'"
                                text-black
                            >
                                <a href="mailto:info@banyaiconsulting.hu">info@banyaiconsulting.hu</a>
                            </ContactDetail>
                            <ContactDetail
                                :icon="require('~/assets/images/icons/phone-blue.svg')"
                                :label="$t('contact.phone') + ':'"
                                text-black
                            >
                                <a href="tel:+3619506081">+36 1 950 60 81</a>
                            </ContactDetail>
                        </div>
                    </SectionWrapper>
                </div>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* FAQ */}}
        {{/* * */}}
        <div class="bg-white text-black pt-16 md:pt-20 pb-16 md:pb-28 lg:pb-36">
            <div class="container">
                <div class="grid grid-cols-12 gap-5 mb-12">
                    <div class="col-span-12 lg:col-span-10 lg:col-start-2 xl:col-span-8 xl:col-start-3 text-center">
                        <h2 class="title title-lg decor-underline decor-underline--magenta mb-8 mx-auto inline-block">
                            {{ $t('indian_dentists.faq.title') }}
                        </h2>
                        <p>{{ $t('indian_dentists.faq.description_1') }}</p>
                        <p class="mt-6">{{ $t('indian_dentists.faq.description_2') }}</p>
                    </div>
                </div>

                <div class="grid grid-cols-12">
                    <div class="col-span-12">
                        <SectionFaq :categories="faqCategories" />
                    </div>
                </div>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Guarantee */}}
        {{/* * */}}
        <div class="bg-gray-100 pt-16 pb-20 md:pt-24 md:pb-32 xl:pt-28 xl:pb-36">
            <SectionWrapper :title="$t('indian_dentists.guarantee.title')" theme-light centered-title class="container">
                <div class="grid grid-cols-12 gap-5">
                    <div class="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
                        <div
                            class="text-gray-800 sm:text-center mb-8"
                            v-html="$t('indian_dentists.guarantee.description')"
                        ></div>
                        <div class="sm:text-center">
                            <button type="button" class="p-component p-button p-button-primary" @click="openContactModal">
                                <span class="p-button-label"> {{ $t('generic.contact_us') }} </span>
                            </button>
                        </div>
                    </div>
                </div>
            </SectionWrapper>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Partners */}}
        {{/* * */}}
        <div
            v-if="partners && partners.length"
            class="bg-white theme-light pt-16 md:pt-20 lg:pt-24 pb-16 md:pb-28 lg:pb-36"
        >
            <SectionPartners
                :partners="partners"
                :title="$t('case_studies.references_block_title')"
                :description="$t('case_studies.references_block_description')"
                theme-light
                class="container"
            />
            <div class="text-center">
                <button type="button" class="p-component p-button p-button-primary" @click="openContactModal">
                    <span class="p-button-label"> {{ $t('generic.contact_cta_variants.variant_4') }} </span>
                </button>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Testimonials */}}
        {{/* * */}}
        <div v-if="false" class="relative pb-12">
            <SectionTestimonials
                v-if="testimonials && testimonials.length"
                :testimonials="testimonials"
                class="relative z-10"
            />
            <div class="absolute bottom-0 left-0 w-full h-36 bg-gray-800 z-0"></div>
        </div>

        <BaseModal :close="closeContactModal" :is-open="isContactModalOpen">
            <ContactForm is-modal />
        </BaseModal>
    </div>
</template>

<script>
import Hero from '@/components/Heroes/Hero';
import FeatureCard from '@/components/Cards/FeatureCard/FeatureCard';
import SectionWrapper from '@/components/UI/SectionWrapper';
import SectionFaq from '@/components/Sections/SectionFaq/SectionFaq';
import SectionTestimonials from '@/components/Sections/SectionTestimonials/SectionTestimonials';
import SectionPartners from '@/components/Sections/SectionPartners/SectionPartners';
import { workforcePartners } from '~/data/partners';
import { faqCategories } from '~/data/indian-dentists-faq';
import ContactDetail from '@/components/Generic/ContactDetail';
import BaseModal from '@/components/Modals/BaseModal.vue';
import ContactForm from '@/components/Forms/ContactForm';

export default {
    name: 'IndianDentists',
    components: {
        Hero,
        FeatureCard,
        SectionWrapper,
        SectionFaq,
        SectionTestimonials,
        ContactDetail,
        SectionPartners,
        BaseModal,
        ContactForm,
    },
    async asyncData({ $axios, redirect, localePath, i18n }) {
        if (!['hu', 'en', 'de'].includes(i18n.locale)) {
            return redirect(localePath('/'));
        }

        let testimonials = null;

        try {
            await $axios.$get('/testimonial?type=1').then((testimonialRes) => {
                if (testimonialRes.success === 1) {
                    testimonials = testimonialRes.data;
                }
            });
        } catch (err) {
            console.log(err);
        }

        return { testimonials };
    },
    data() {
        return {
            isContactModalOpen: false,
            partners: workforcePartners,
        };
    },
    head() {
        return {
            title: this.$t('indian_dentists.lead'),
            meta: [
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: this.$getOgTitle(this.$t('indian_dentists.lead')),
                },
                {
                    hid: 'description',
                    name: 'description',
                    content: this.$t('indian_dentists.description'),
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: this.$t('indian_dentists.description'),
                },
            ],
        };
    },
    computed: {
        faqCategories() {
            return faqCategories[this.$i18n.locale] || faqCategories.hu;
        },
    },
    methods: {
        openContactModal() {
            this.isContactModalOpen = true;
        },
        closeContactModal() {
            this.isContactModalOpen = false;
        },
    },
};
</script>

<style scoped></style>
